import { useMutation } from '@tanstack/react-query'
import { useCallback } from 'react'
import { queryClient, useTRPC } from '~/services/trpc'

import { toast } from '@hap/components'
import { EvaluationCriteriaDisputeLabel } from '@hap/constants'

export interface DisputeInput {
  scorecardCriteriaAssignmentId: string
  proposedAwardedPoints: number | null
  note?: string | null
  label?: EvaluationCriteriaDisputeLabel | null
}

export namespace EvaluationCriteriaDisputeMutators {
  export function useCreate(opts: { contactLogId: string; evaluationId: string }) {
    const trpc = useTRPC()

    const { mutateAsync, isPending } = useMutation(
      trpc.evaluationCriteriaDisputes.create.mutationOptions({
        onSuccess: () => {
          toast.success('Dispute submitted')
          queryClient.invalidateQueries({
            queryKey: trpc.evaluations.getByContactLogId.queryKey(opts.contactLogId),
          })
          queryClient.invalidateQueries({
            queryKey: trpc.evaluationCriteriaDisputes.listByEvaluationId.queryKey(
              opts.evaluationId,
            ),
          })
        },
        onError: (error) => {
          toast.error('Failed to submit dispute: ' + error.message)
        },
      }),
    )

    const create = useCallback(
      async (input: DisputeInput) => {
        return mutateAsync({
          evaluationId: opts.evaluationId,
          scorecardCriteriaAssignmentId: input.scorecardCriteriaAssignmentId,
          proposedAwardedPoints: input.proposedAwardedPoints,
          note: input.note,
        })
      },
      [mutateAsync, opts.evaluationId],
    )

    return { create, isPending }
  }

  export function useCreateBatch(opts: { contactLogId: string; evaluationId: string }) {
    const trpc = useTRPC()

    const { mutateAsync, isPending } = useMutation(
      trpc.evaluationCriteriaDisputes.createBatch.mutationOptions({
        onSuccess: () => {
          toast.success('Disputes submitted successfully')
          queryClient.invalidateQueries({
            queryKey: trpc.evaluations.getByContactLogId.queryKey(opts.contactLogId),
          })
          queryClient.invalidateQueries({
            queryKey: trpc.evaluations.fromId.queryKey(opts.evaluationId),
          })
          queryClient.invalidateQueries({
            queryKey: trpc.evaluationCriteriaDisputes.listByEvaluationId.queryKey(
              opts.evaluationId,
            ),
          })
        },
        onError: (error) => {
          toast.error('Failed to submit disputes: ' + error.message)
        },
      }),
    )

    const createBatch = useCallback(
      async (disputes: DisputeInput[]) => {
        return mutateAsync({
          evaluationId: opts.evaluationId,
          disputes,
        })
      },
      [mutateAsync, opts.evaluationId],
    )

    return { createBatch, isPending }
  }

  export function useResolve(opts: { contactLogId: string; evaluationId: string }) {
    const trpc = useTRPC()

    const { mutateAsync, isPending } = useMutation(
      trpc.evaluationCriteriaDisputes.resolve.mutationOptions({
        onSuccess: () => {
          toast.success('Dispute resolved and score updated')
          queryClient.invalidateQueries({
            queryKey: trpc.evaluations.getByContactLogId.queryKey(opts.contactLogId),
          })
          queryClient.invalidateQueries({
            queryKey: trpc.evaluationCriteriaDisputes.listByEvaluationId.queryKey(
              opts.evaluationId,
            ),
          })
        },
        onError: (error) => {
          toast.error('Failed to resolve dispute: ' + error.message)
        },
      }),
    )

    const resolve = useCallback(
      async (input: {
        disputeId: string
        finalAwardedPoints: number | null
        resolutionNote?: string | null
      }) => mutateAsync(input),
      [mutateAsync],
    )

    return { resolve, isPending }
  }
}
